'use client'

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { Bookmark, Clock, ChevronRight, Trash2, BookOpen } from 'lucide-react'

export interface SavedResourceItem {
  canonicalPath: string
  title: string
  bookTitle?: string
  kind?: string
  timestamp: number
}

export const RECENT_STORAGE_KEY = 'wlib_recent_history_v1'
export const BOOKMARKS_STORAGE_KEY = 'wlib_bookmarks_v1'

export function LibraryRecentBar() {
  const [recent, setRecent] = useState<SavedResourceItem[]>([])
  const [bookmarks, setBookmarks] = useState<SavedResourceItem[]>([])
  const [activeTab, setActiveTab] = useState<'recent' | 'bookmarks'>('recent')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    try {
      const rawRecent = localStorage.getItem(RECENT_STORAGE_KEY)
      const rawBookmarks = localStorage.getItem(BOOKMARKS_STORAGE_KEY)
      const parsedRecent: SavedResourceItem[] = rawRecent ? JSON.parse(rawRecent) : []
      const parsedBookmarks: SavedResourceItem[] = rawBookmarks ? JSON.parse(rawBookmarks) : []
      setRecent(parsedRecent)
      setBookmarks(parsedBookmarks)
      if (parsedBookmarks.length > 0 && parsedRecent.length === 0) {
        setActiveTab('bookmarks')
      }
    } catch {
      // Ignore storage errors
    }
  }, [])

  if (!mounted || (recent.length === 0 && bookmarks.length === 0)) {
    return null
  }

  const clearRecent = () => {
    try {
      localStorage.removeItem(RECENT_STORAGE_KEY)
      setRecent([])
      if (bookmarks.length > 0) setActiveTab('bookmarks')
    } catch {
      // Ignore
    }
  }

  const removeBookmark = (path: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      const next = bookmarks.filter((b) => b.canonicalPath !== path)
      localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(next))
      setBookmarks(next)
      if (next.length === 0 && recent.length > 0) setActiveTab('recent')
    } catch {
      // Ignore
    }
  }

  const items = activeTab === 'recent' ? recent.slice(0, 4) : bookmarks.slice(0, 8)

  return (
    <div className="wl-recent-bar">
      <div className="wl-recent-bar-header">
        <div className="wl-recent-tabs">
          {recent.length > 0 && (
            <button
              type="button"
              className={`wl-recent-tab ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
            >
              <Clock size={14} />
              <span>Continue Reading ({recent.length})</span>
            </button>
          )}
          {bookmarks.length > 0 && (
            <button
              type="button"
              className={`wl-recent-tab ${activeTab === 'bookmarks' ? 'active' : ''}`}
              onClick={() => setActiveTab('bookmarks')}
            >
              <Bookmark size={14} />
              <span>Saved Bookmarks ({bookmarks.length})</span>
            </button>
          )}
        </div>

        {activeTab === 'recent' && recent.length > 0 && (
          <button
            type="button"
            className="wl-recent-clear"
            onClick={clearRecent}
            title="Clear recent reading history"
          >
            <Trash2 size={12} />
            <span>Clear History</span>
          </button>
        )}
      </div>

      <div className="wl-recent-grid">
        {items.map((item) => (
          <Link key={item.canonicalPath} href={item.canonicalPath} className="wl-recent-item">
            <div style={{ minWidth: 0, flex: 1 }}>
              {item.bookTitle && (
                <div className="wl-recent-item-book">
                  <BookOpen size={11} style={{ flexShrink: 0 }} />
                  <span>{item.bookTitle}</span>
                </div>
              )}
              <div className="wl-recent-item-title">{item.title}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
              {activeTab === 'bookmarks' && (
                <button
                  type="button"
                  onClick={(e) => removeBookmark(item.canonicalPath, e)}
                  className="wl-recent-remove-btn"
                  title="Remove bookmark"
                  aria-label="Remove bookmark"
                >
                  <Trash2 size={13} />
                </button>
              )}
              <ChevronRight size={15} style={{ color: 'var(--purple-primary)' }} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
